import { useEffect, useState } from 'react';
import type { HistoryEntry, ParameterTarget } from '../../services/measurementService';
import type { SeriesColourSlot } from './parameterColours';

/**
 * Pure helpers of the multi-parameter history charts (#1653): a shared
 * timeline for the synchronised small multiples, the value of a parameter on a
 * hovered day, and the "% of target range" normalisation of the combined view.
 */

export interface ChartSeries {
  readonly parameterId: string;
  readonly label: string;
  readonly unit: string;
  /** Oldest first. */
  readonly points: readonly HistoryEntry[];
  readonly target?: ParameterTarget;
  readonly slot: SeriesColourSlot;
}

/** One x position of the shared time axis: a timestamp and the calendar day it belongs to. */
export interface TimelinePoint {
  readonly time: number;
  readonly day: string;
}

const pad = (n: number): string => String(n).padStart(2, '0');

/**
 * Calendar day ('YYYY-MM-DD') of a value as the user sees it: the date of a
 * date-only value, else the local date of its time.
 */
export function dayKey(entry: Pick<HistoryEntry, 'measuredAt' | 'measuredDate'>): string {
  if (entry.measuredDate && /^\d{4}-\d{2}-\d{2}$/.test(entry.measuredDate)) return entry.measuredDate;
  const d = new Date(entry.measuredAt);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export function entryTime(entry: Pick<HistoryEntry, 'measuredAt'>): number {
  return Date.parse(entry.measuredAt);
}

/**
 * Every timestamp of every series, oldest first — the common x values of all
 * charts. Recharts' value-based sync (syncMethod="value") matches the hovered
 * x value exactly, so each chart must have a data row at every timestamp
 * (null where its parameter has no value) for the crosshair to land on the
 * same date in all of them.
 */
export function sharedTimeline(series: readonly ChartSeries[]): TimelinePoint[] {
  const byTime = new Map<number, string>();
  for (const s of series) {
    for (const p of s.points) {
      const time = entryTime(p);
      if (Number.isFinite(time) && !byTime.has(time)) byTime.set(time, dayKey(p));
    }
  }
  return [...byTime.entries()].sort((a, b) => a[0] - b[0]).map(([time, day]) => ({ time, day }));
}

/** [first, last] timestamp of the shared timeline (the same range on every chart), or null when empty. */
export function timeDomain(timeline: readonly TimelinePoint[]): [number, number] | null {
  if (timeline.length === 0) return null;
  return [timeline[0]!.time, timeline[timeline.length - 1]!.time];
}

/**
 * The value of a series shown for a hovered timestamp: the value measured at
 * exactly that time, else the value of the same calendar day closest in time,
 * else null ("no value on this day") — never a value from another day.
 */
export function entryForDay(points: readonly HistoryEntry[], at: TimelinePoint): HistoryEntry | null {
  let best: HistoryEntry | null = null;
  let bestDistance = Number.POSITIVE_INFINITY;
  for (const p of points) {
    if (dayKey(p) !== at.day) continue;
    const distance = Math.abs(entryTime(p) - at.time);
    if (distance < bestDistance) {
      best = p;
      bestDistance = distance;
    }
  }
  return best;
}

/** Value of `points` at exactly each timeline position (null elsewhere), for one chart's data rows. */
export function valuesOnTimeline(points: readonly HistoryEntry[], timeline: readonly TimelinePoint[]): Array<number | null> {
  const byTime = new Map<number, number>();
  for (const p of points) byTime.set(entryTime(p), p.value);
  return timeline.map((t) => byTime.get(t.time) ?? null);
}

/** A value as % of its target range: 0 % = lower bound, 100 % = upper bound (may be < 0 or > 100). */
export function percentOfTarget(value: number, target: ParameterTarget): number | null {
  const span = target.max - target.min;
  if (!(span > 0)) return null;
  return ((value - target.min) / span) * 100;
}

/** True when the browser asks for reduced motion — the charts then never animate. */
export function usePrefersReducedMotion(): boolean {
  const query = '(prefers-reduced-motion: reduce)';
  const [reduced, setReduced] = useState<boolean>(
    () => typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia(query).matches,
  );
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const mql = window.matchMedia(query);
    setReduced(mql.matches);
    const onChange = (event: MediaQueryListEvent): void => setReduced(event.matches);
    mql.addEventListener?.('change', onChange);
    return () => mql.removeEventListener?.('change', onChange);
  }, []);
  return reduced;
}

/** Current value of a design token (CSS custom property on <html>). */
export function readCssVar(name: string): string {
  if (typeof window === 'undefined' || typeof document === 'undefined') return '';
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim();
}

/**
 * Y range with some headroom, snapped to a "nice" step (1, 2 or 5 × 10^n) so
 * the axis ticks read as round numbers. Never below `floor` (water values are ≥ 0).
 */
export function niceDomain(min: number, max: number, floor: number | null = 0): { domain: [number, number]; ticks: number[] } {
  const span = max - min || Math.abs(max) || 1;
  const raw = span / 4;
  const magnitude = 10 ** Math.floor(Math.log10(raw));
  const step = [1, 2, 5, 10].map((m) => m * magnitude).find((s) => s >= raw) ?? 10 * magnitude;
  const lowest = Math.floor((min - span * 0.05) / step) * step;
  const lower = floor === null ? lowest : Math.max(floor, lowest);
  const upper = Math.ceil((max + span * 0.05) / step) * step;
  const ticks: number[] = [];
  for (let v = lower; v <= upper + step / 2; v += step) ticks.push(Number(v.toPrecision(12)));
  return { domain: [Number(lower.toPrecision(12)), Number(upper.toPrecision(12))], ticks };
}
